import React, { FC, memo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { createUseStyles } from "react-jss";

import Close from "Assets/images/close.svg";

import { MediaObject } from "Components/MediaObject";
import InstagramObject from "Components/InstagramObject";
import MediaIcon from "Components/MediaIcon";
import Photo from "Assets/images/photo_icon.svg";
import Video from "Assets/images/video_icon.svg";
import Pano from "Assets/images/pano_icon.svg";

import { AudioPlayer } from "Containers/AudioPlayer";
import MediaViewerContainer, {
  setShowMediaViewer,
  setShowSmallViewPort,
  setDisplay,
} from "Containers/MediaViewer";

import { areEqualShallow } from "Utils/areEqual";
import { useDispatch, useSelector } from "react-redux";
import { setStopState, StopState } from "Containers/Stops";

import AudioPlay from "Assets/images/audio_play.svg";
import { getOneTrustSafeImgSrc, isEnterOrSpace } from "Utils/utils";
import { sanitize } from "dompurify";
import GoogleMap from "../../map/GoogleMap";
import styles from "./styles";

interface Props {
  stop: any;
  setCurrentStop: (id: number) => void;
  scene: boolean;
  setScene: (val: boolean) => void;
  resetAudio: () => void;
  locId: any;
  mapObj: GoogleMap;
}

const getStopThumbnail = (stop) => {
  if (stop?.photos?.length > 0) {
    return stop.photoThumb;
  }
  if (stop?.videos?.length > 0) {
    return (
      (stop.videoThumb && getOneTrustSafeImgSrc(stop.videoThumb)) ||
      stop.videos[0].smallurl
    );
  }
  if (stop?.panoramas?.length > 0) {
    return stop.panoThumb || stop.thumbnail;
  }

  return stop.thumbnail;
};

const Scene: FC<Props> = memo((props: Props) => {
  const {
    stop,
    setCurrentStop,
    scene,
    setScene,
    resetAudio,
    locId,
    mapObj,
  } = props;
  const useStyles = createUseStyles(styles);
  const [open, setIsOpen] = useState(true);

  const { showMediaViewer, showSmallViewPort } = useSelector(
    (state: any) => state.mediaViewer
  );
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const handleClose = () => {
    resetAudio();
    setCurrentStop(-1);
    dispatch(setStopState([stop.id], StopState.Visited));
  };

  const handleMobileScene = () => {
    setScene(true);
    setIsOpen(true);
  };

  useEffect(() => {
    const bottomStopNavTitle = document.getElementById("bottomNavTitle");
    if (bottomStopNavTitle) bottomStopNavTitle.focus();
  }, [open]);

  // Close this Scene if user clicks on the map where there is
  // no location outlined.
  useEffect(() => {
    let clickListener;
    let dragListener;
    let touchListener;
    if (mapObj) {
      clickListener = window.google.maps.event.addListener(
        mapObj.map,
        "click",
        () => setIsOpen(false)
      );

      dragListener = window.google.maps.event.addListener(
        mapObj.map,
        "mousedown",
        () => setIsOpen(false)
      );

      touchListener = window.google.maps.event.addListener(
        mapObj.map,
        "touchstart",
        () => setIsOpen(false)
      );
    }

    return () => {
      if (clickListener) {
        window.google.maps.event.removeListener(clickListener);
      }
      if (dragListener) {
        window.google.maps.event.removeListener(dragListener);
      }
      if (touchListener) {
        window.google.maps.event.removeListener(touchListener);
      }
    };
  }, [mapObj]);

  const totalMediaObjects =
    stop.panoramas?.length + stop.videos?.length + stop.photos?.length;

  const sceneAnimations = {
    open: {
      y: "0%",
      transition: {
        ease: [0.28, 0.57, 0.2, 1],
        duration: 0.5,
      },
    },
    closed: {
      y: "120%",
      transition: {
        ease: "easeIn",
        duration: 0.25,
        onComplete: () => {
          handleClose();
        },
      },
    },
  };

  return (
    <>
      <motion.div
        id="bottomStopNav"
        initial={{ y: "120%" }}
        animate={open ? "open" : "closed"}
        variants={sceneAnimations}
        className={classes.container}
        data-testid="container"
      >
        <div className={classes.headerContainer}>
          <h3
            id="bottomNavTitle"
            tabIndex={0}
            title={stop.title}
            className={classes.title}
          >
            {stop.title}
          </h3>
          {stop.audio && (
            <span className={classes.audioPlayer}>
              <AudioPlayer url={stop.audio} icon={AudioPlay} />
            </span>
          )}

          <span
            aria-label="Close"
            role="button"
            tabIndex={0}
            className={classes.closeIcon}
            onClick={() => {
              setIsOpen(false);
            }}
            onKeyDown={(event) => {
              if (isEnterOrSpace(event)) {
                setIsOpen(false);
              }
            }}
          >
            <Close className={classes.closeScene} />
          </span>
        </div>
        <div className={classes.footerContainer}>
          {stop.fullMapDescription && (
            /* eslint-disable react/no-danger */
            <div
              className={classes.description}
              dangerouslySetInnerHTML={{
                __html: sanitize(stop.fullMapDescription),
              }}
            />
          )}
          {totalMediaObjects > 0 && (
            <div className={classes.mediaContainer}>
              {stop?.panoramas?.length > 0 && (
                <MediaObject
                  alt={`View panoramas for ${stop.title}`}
                  img={stop.panoThumb || stop.thumbnail}
                  onClick={() => {
                    resetAudio();
                    dispatch(setShowSmallViewPort(false));
                    dispatch(setShowMediaViewer("panorama"));
                  }}
                  style={{ borderRadius: "8px" }}
                >
                  <MediaIcon text={stop.panoramas.length} Icon={Pano} />
                </MediaObject>
              )}
              {stop?.videos?.length > 0 && (
                <>
                  {stop.videoThumb?.includes("instagram") ? (
                    <div className={classes.instagramContainer}>
                      <InstagramObject
                        alt={`View videos for ${stop.title}`}
                        embedId={stop.videoThumb.match(/<\?= (.*?); \?>/)[1]}
                        onClick={() => {
                          resetAudio();
                          dispatch(setShowSmallViewPort(false));
                          dispatch(setShowMediaViewer("video"));
                        }}
                        top={-75}
                        left={-18}
                        width="100%"
                        height="100%"
                        iframeWidth="150%"
                        iframeHeight="200%"
                        style={{ borderRadius: "8px" }}
                      >
                        <MediaIcon text={stop.videos.length} Icon={Video} />
                      </InstagramObject>
                    </div>
                  ) : (
                    <MediaObject
                      alt={`View videos for ${stop.title}`}
                      img={getOneTrustSafeImgSrc(
                        stop.videoThumb || stop.videos[0].smallurl
                      )}
                      onClick={() => {
                        resetAudio();
                        dispatch(setShowSmallViewPort(false));
                        dispatch(setShowMediaViewer("video"));
                      }}
                      style={{ borderRadius: "8px" }}
                      media={stop.videos}
                    >
                      <MediaIcon text={stop.videos.length} Icon={Video} />
                    </MediaObject>
                  )}
                </>
              )}
              {stop?.photos?.length > 0 && (
                <>
                  {stop.photoThumb.includes("instagram") ? (
                    <div className={classes.instagramContainer}>
                      <InstagramObject
                        alt={`View photos for ${stop.title}`}
                        embedId={stop.photoThumb.match(/<\?= (.*?); \?>/)[1]}
                        onClick={() => {
                          dispatch(setShowSmallViewPort(false));
                          dispatch(setShowMediaViewer("photo"));
                        }}
                        top={-55}
                        left={0}
                        width="100%"
                        height="100%"
                        iframeWidth="150%"
                        iframeHeight="170%"
                        style={{ borderRadius: "8px" }}
                      >
                        <MediaIcon text={stop.photos.length} Icon={Photo} />
                      </InstagramObject>
                    </div>
                  ) : (
                    <MediaObject
                      alt={`View photos for ${stop.title}`}
                      img={stop.photoThumb}
                      onClick={() => {
                        dispatch(setShowSmallViewPort(false));
                        dispatch(setShowMediaViewer("photo"));
                      }}
                      style={{ borderRadius: "8px" }}
                      media={stop.photos}
                    >
                      <MediaIcon text={stop.photos.length} Icon={Photo} />
                    </MediaObject>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {scene && (
        <motion.div
          initial={{ y: "120%" }}
          animate={open ? "open" : "closed"}
          variants={sceneAnimations}
          className={classes.smallContainer}
          onClick={() => {
            dispatch(setShowSmallViewPort(true));
            dispatch(setShowMediaViewer("photo"));
            setScene(false);
            resetAudio();
            dispatch(setDisplay(true));
          }}
        >
          {totalMediaObjects > 0 && (
            <div className={classes.imageContainer}>
              {getStopThumbnail(stop)?.includes("instagram") ? (
                <div className={classes.instagramContainer}>
                  <InstagramObject
                    alt={`View photos for ${stop.title}`}
                    embedId={getStopThumbnail(stop).match(/<\?= (.*?); \?>/)[1]}
                    onClick={() => {
                      dispatch(setShowSmallViewPort(false));
                      dispatch(setShowMediaViewer("photo"));
                    }}
                    top={-55}
                    left="0"
                    width="100%"
                    height="100%"
                    iframeWidth="150%"
                    iframeHeight="170%"
                  >
                    <MediaIcon text={totalMediaObjects.toString()}>
                      <div className={classes.imageTextContainer}>
                        <span className={classes.imageText}>
                          {`+${totalMediaObjects}`}
                        </span>
                      </div>
                    </MediaIcon>
                  </InstagramObject>
                </div>
              ) : (
                <MediaObject
                  alt={`View media for ${stop.title}`}
                  img={getStopThumbnail(stop)}
                  style={{
                    borderTopLeftRadius: "8px",
                    borderBottomLeftRadius: "8px",
                  }}
                  media={[...stop.photos, ...stop.videos, ...stop.panoramas]}
                >
                  <MediaIcon text={totalMediaObjects.toString()}>
                    <div className={classes.imageTextContainer}>
                      <span className={classes.imageText}>
                        {`+${totalMediaObjects}`}
                      </span>
                    </div>
                  </MediaIcon>
                </MediaObject>
              )}
            </div>
          )}
          <div className={classes.descriptionContainer}>
            <span className={classes.smallTitle}>{stop.title}</span>
            {stop.audio && (
              <span className={classes.audioPlayer}>
                <AudioPlayer url={stop.audio} icon={AudioPlay} />
              </span>
            )}
            {stop.fullMapDescription && (
              <div
                className={classes.smallDescription}
                dangerouslySetInnerHTML={{
                  __html: sanitize(stop.fullMapDescription),
                }}
              />
            )}
          </div>
        </motion.div>
      )}

      {showMediaViewer && (
        <>
          <div>
            <MediaViewerContainer
              stop={stop}
              mediaViewer={showMediaViewer}
              setShowMediaViewer={setShowMediaViewer}
              smallViewPort={showSmallViewPort}
              locId={locId}
              setSceneOpen={handleMobileScene}
            />
          </div>
        </>
      )}
    </>
  );
}, areEqualShallow);
export default Scene;
try {
    // @ts-ignore
    Scene.displayName = "Scene";
    // @ts-ignore
    Scene.__docgenInfo = { "description": "", "displayName": "Scene", "props": { "stop": { "defaultValue": null, "description": "", "name": "stop", "required": true, "type": { "name": "any" } }, "setCurrentStop": { "defaultValue": null, "description": "", "name": "setCurrentStop", "required": true, "type": { "name": "(id: number) => void" } }, "scene": { "defaultValue": null, "description": "", "name": "scene", "required": true, "type": { "name": "boolean" } }, "setScene": { "defaultValue": null, "description": "", "name": "setScene", "required": true, "type": { "name": "(val: boolean) => void" } }, "resetAudio": { "defaultValue": null, "description": "", "name": "resetAudio", "required": true, "type": { "name": "() => void" } }, "locId": { "defaultValue": null, "description": "", "name": "locId", "required": true, "type": { "name": "any" } }, "mapObj": { "defaultValue": null, "description": "", "name": "mapObj", "required": true, "type": { "name": "GoogleMap" } } } };
    // @ts-ignore
    if (typeof STORYBOOK_REACT_CLASSES !== "undefined")
        // @ts-ignore
        STORYBOOK_REACT_CLASSES["src/shared/components/Scene/Scene.tsx#Scene"] = { docgenInfo: Scene.__docgenInfo, name: "Scene", path: "src/shared/components/Scene/Scene.tsx#Scene" };
}
catch (__react_docgen_typescript_loader_error) { }